import { useState, useEffect, useCallback, useRef } from 'react';
import { useTwilioCall } from './useTwilioCall';
import { useBackendTranscription } from './useBackendTranscription';
import { useBrowserTranscription } from './useBrowserTranscription';
import { useToast } from '@/hooks/use-toast';

export type { TranscriptMessage } from '@/types/speechRecognition';

export const useTwilioTranscription = (callInProgress?: boolean, callSid?: string | null, localPhoneNumber?: string, remotePhoneNumber?: string) => {
  // Use passed parameters or fallback to direct hook call
  const twilioState = useTwilioCall();
  const actualCallInProgress = callInProgress ?? twilioState.callInProgress;
  const actualCallSid = callSid ?? twilioState.callSid;
  
  const { toast } = useToast();
  const [hasAttemptedAutoStart, setHasAttemptedAutoStart] = useState(false);
  const previousCallSidRef = useRef<string | null>(null);
  
  console.log('🎯 [TRANSCRIPTION] Hook called with:', { 
    passedCallInProgress: callInProgress,
    passedCallSid: callSid,
    actualCallInProgress, 
    actualCallSid, 
    hasAttemptedAutoStart,
    timestamp: new Date().toISOString() 
  });
  
  // Use browser transcription hook
  const {
    transcript: browserTranscripts,
    isListening,
    isSupported,
    startTranscription,
    stopTranscription,
    clearTranscript: clearBrowserTranscript
  } = useBrowserTranscription(localPhoneNumber);
  
  console.log('🎯 [TRANSCRIPTION] Browser state:', { 
    isListening, 
    isSupported, 
    browserTranscriptCount: browserTranscripts.length 
  });

  // Use backend transcription hook
  const {
    backendTranscripts,
    backendConnected,
    connectToBackendTranscription,
    disconnectFromBackendTranscription,
    clearBackendTranscripts
  } = useBackendTranscription(actualCallSid, actualCallInProgress, remotePhoneNumber);
  
  console.log('🎯 [TRANSCRIPTION] Backend state:', { 
    backendConnected, 
    backendTranscriptCount: backendTranscripts.length 
  });

  // Auto-start transcription when CallSid becomes available
  useEffect(() => {
    console.log('🎬 [AUTO-START] Effect triggered with:', { 
      actualCallInProgress, 
      actualCallSid, 
      hasAttemptedAutoStart,
      isSupported,
      isListening 
    });
    
    // CRITICAL FIX: Reset hasAttemptedAutoStart when callSid changes to allow re-attempts
    if (actualCallSid && actualCallSid !== previousCallSidRef.current) {
      console.log('🎬 [AUTO-START] 🔄 New callSid detected, resetting auto-start flag');
      console.log('🎬 [AUTO-START] Previous:', previousCallSidRef.current, 'New:', actualCallSid);
      setHasAttemptedAutoStart(false);
      previousCallSidRef.current = actualCallSid;
    }
    
    if (actualCallInProgress && actualCallSid && !hasAttemptedAutoStart) {
      console.log('🎬 [AUTO-START] ✅ Starting transcription automatically for CallSid:', actualCallSid);
      
      // Start browser transcription if supported
      if (isSupported && !isListening) {
        console.log('🎬 [AUTO-START] 🎤 Starting browser transcription...');
        startTranscription().catch(error => {
          console.error('🎬 [AUTO-START] ❌ Browser transcription failed:', error);
        });
      } else {
        console.log('🎬 [AUTO-START] ⚠️ Browser transcription not started:', { isSupported, isListening });
      }
      
      // Connect to backend transcription WebSocket
      console.log('🎬 [AUTO-START] 🔌 Connecting to backend transcription...');
      connectToBackendTranscription();
      
      setHasAttemptedAutoStart(true);
      
      toast({
        title: "Transcription Started",
        description: "Live call transcription is now active",
      });
    } else if (!actualCallInProgress && hasAttemptedAutoStart) {
      console.log('🎬 [AUTO-START] 📴 Call ended, resetting and disconnecting...');
      // Reset auto-start flag when call ends
      setHasAttemptedAutoStart(false);
      disconnectFromBackendTranscription();
    } else {
      console.log('🎬 [AUTO-START] ❌ Conditions not met for auto-start:', {
        actualCallInProgress,
        actualCallSid: !!actualCallSid,
        hasAttemptedAutoStart
      });
    }
    
    return () => {
      if (!actualCallInProgress) {
        disconnectFromBackendTranscription();
      }
    };
  }, [actualCallInProgress, actualCallSid, hasAttemptedAutoStart, isSupported, isListening, startTranscription, connectToBackendTranscription, disconnectFromBackendTranscription, toast]);

  // Handle call end cleanup
  useEffect(() => {
    if (!actualCallInProgress && isListening) {
      console.log('📴 [CLEANUP] Call ended, stopping browser transcription');
      stopTranscription();
    }
  }, [actualCallInProgress, isListening, stopTranscription]);

  const clearTranscript = useCallback(() => {
    console.log('🧹 [CLEANUP] Clearing all transcripts');
    clearBrowserTranscript();
    clearBackendTranscripts();
    setHasAttemptedAutoStart(false);
  }, [clearBrowserTranscript, clearBackendTranscripts]);

  // Merge and sort all transcripts by timestamp
  const allTranscripts = [...browserTranscripts, ...backendTranscripts]
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const downloadTranscript = useCallback(() => {
    const transcriptText = allTranscripts
      .map(msg => `[${msg.timestamp.toLocaleTimeString()}] ${msg.speaker.toUpperCase()}: ${msg.text}`)
      .join('\n');
    
    const blob = new Blob([transcriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-transcript-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [allTranscripts]);

  return {
    transcript: allTranscripts,
    isListening,
    isSupported,
    startTranscription,
    stopTranscription,
    clearTranscript,
    downloadTranscript,
    backendConnected
  };
};